import type { TokenStore } from "@/domains/tokens/token-store"
import { priceToSqrtRatioX96 } from "@/domains/manual-cl-core"

/**
 * Calculate sqrtPriceX96 from a price ratio expressed as "token1 per 1 token0"
 * (or inverted if `invert` is true). Delegates to the bit-exact Uniswap-SDK wrapper
 * in manual-cl-core so we always match Uniswap's reference encoding.
 *
 * IMPORTANT: callers must pass `token0` / `token1` in canonical (address-sorted)
 * pool order. See `getCanonicalPoolOrder` below.
 *
 * @param price - Human-readable price (e.g. "2500.35")
 * @param token0 - Canonical pool token0
 * @param token1 - Canonical pool token1
 * @param invert - If true, the supplied price is expressed as "token0 per 1 token1"
 *                 instead of the canonical "token1 per 1 token0"
 * @returns sqrtPriceX96 as bigint, or undefined on invalid input
 */
export function calculateSqrtPriceX96(
  price: string,
  token0: TokenStore,
  token1: TokenStore,
  invert: boolean = false,
): bigint | undefined {
  try {
    return priceToSqrtRatioX96({ price, token0, token1, invert })
  } catch (error) {
    console.error("[calculateSqrtPriceX96] Error calculating sqrtPriceX96:", error)
    return undefined
  }
}

/**
 * Return `[token0, token1]` in canonical (address-sorted) Uniswap order.
 * Also returns an `invert` flag the caller can pass to `calculateSqrtPriceX96`
 * when the user-typed price is expressed relative to the selection order
 * rather than the canonical order.
 */
export function getCanonicalPoolOrder(
  a: TokenStore,
  b: TokenStore,
): { token0: TokenStore; token1: TokenStore; inverted: boolean } {
  const aAddr = a.address.toString().toLowerCase()
  const bAddr = b.address.toString().toLowerCase()
  if (aAddr < bAddr) {
    return { token0: a, token1: b, inverted: false }
  }
  return { token0: b, token1: a, inverted: true }
}

/**
 * Get market price for a token pair, expressed as "token1 per 1 token0"
 * using apiPrice. Returns undefined when either side has no price.
 * Callers pass tokens in canonical order.
 */
export async function getMarketPrice(
  token0: TokenStore,
  token1: TokenStore,
): Promise<number | undefined> {
  if (token0.apiPrice > 0 && token1.apiPrice > 0) {
    return token0.apiPrice / token1.apiPrice
  }
  return undefined
}
